import React, { useRef, useState } from 'react';
import { Upload, Trash2, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { resizeImageFile } from '../lib/imageUtils';
import {
  getProfilePhotos,
  addProfilePhoto,
  deleteProfilePhoto,
  generateId,
} from '../lib/profileStorage';

const CATEGORIES = [
  { id: 'before', label: 'Before' },
  { id: 'during', label: 'During' },
  { id: 'after', label: 'After' },
];

export default function PhotoGallery({ clientId, uploadedBy = 'client', canEdit = true, testIdPrefix = 'photo' }) {
  const [photos, setPhotos] = useState(() => getProfilePhotos(clientId));
  const [activeCat, setActiveCat] = useState('before');
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);

  const filtered = photos.filter((p) => p.category === activeCat);

  const handleFile = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        try {
          const dataUrl = await resizeImageFile(f, 1200, 0.75);
          const photo = {
            id: generateId('ph'),
            category: activeCat,
            dataUrl,
            uploadedAt: new Date().toISOString(),
            uploadedBy,
          };
          const updated = addProfilePhoto(clientId, photo);
          setPhotos([...updated]);
        } catch (e) {
          toast.error(e.message || 'Could not upload one of the photos.');
        }
      }
      toast.success('Photo uploaded.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = (id) => {
    const updated = deleteProfilePhoto(clientId, id);
    setPhotos([...updated]);
    setConfirmDelete(null);
    if (lightbox?.id === id) setLightbox(null);
    toast.success('Photo removed.');
  };

  return (
    <div data-testid={`${testIdPrefix}-gallery`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {CATEGORIES.map((c) => {
          const count = photos.filter((p) => p.category === c.id).length;
          const active = activeCat === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition ${
                active
                  ? 'bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/25'
                  : 'bg-white text-slate-200 border-white/10 hover:border-blue-400'
              }`}
              data-testid={`${testIdPrefix}-cat-${c.id}`}
            >
              {c.label}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 text-xs font-bold rounded-full ${
                active ? 'bg-slate-900/25 text-white' : 'bg-slate-900 text-slate-300'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
        {canEdit && (
          <label
            className={`ml-auto inline-flex items-center gap-2 h-10 px-4 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-sm font-semibold cursor-pointer shadow-md shadow-blue-500/25 ${
              uploading ? 'opacity-60 cursor-wait' : ''
            }`}
            data-testid={`${testIdPrefix}-upload-label`}
          >
            <Camera className="w-4 h-4" />
            {uploading ? 'Uploading…' : `Upload ${CATEGORIES.find((c) => c.id === activeCat).label}`}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files)}
              className="hidden"
              data-testid={`${testIdPrefix}-upload-input`}
            />
          </label>
        )}
      </div>

      {filtered.length === 0 ? (
        <div
          className="rounded-3xl border-2 border-dashed border-blue-500/30 bg-slate-900/40 p-10 text-center text-slate-400"
          data-testid={`${testIdPrefix}-empty-${activeCat}`}
        >
          <Upload className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          No <span className="lowercase">{activeCat}</span> photos yet.
          {canEdit && (
            <p className="text-xs text-slate-400 mt-1">
              Upload one to start your journey.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="relative group rounded-2xl overflow-hidden border border-blue-500/20 bg-white aspect-square cursor-pointer"
              onClick={() => setLightbox(p)}
              data-testid={`${testIdPrefix}-item-${p.id}`}
            >
              <img
                src={p.dataUrl}
                alt={`${p.category} on ${new Date(p.uploadedAt).toLocaleDateString()}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent px-3 py-2 text-[11px] text-white flex items-center justify-between">
                <span>{new Date(p.uploadedAt).toLocaleDateString()}</span>
                <span className="uppercase tracking-widest text-blue-500/30 font-bold">
                  {p.uploadedBy}
                </span>
              </div>
              {canEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(p);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-slate-950/90 text-red-500 hover:bg-slate-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  data-testid={`${testIdPrefix}-delete-${p.id}`}
                  title="Delete photo"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid={`${testIdPrefix}-lightbox`}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-12 right-0 h-10 px-4 rounded-full bg-slate-950/90 hover:bg-slate-800 text-slate-100 text-sm font-semibold inline-flex items-center gap-2"
              data-testid={`${testIdPrefix}-lightbox-close`}
            >
              <X className="w-4 h-4" /> Close
            </button>
            <img
              src={lightbox.dataUrl}
              alt=""
              className="w-full max-h-[80vh] object-contain rounded-2xl"
            />
            <p className="text-center text-white/80 text-sm mt-3">
              {lightbox.category.toUpperCase()} · {new Date(lightbox.uploadedAt).toLocaleString()} · by {lightbox.uploadedBy}
            </p>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6">
              <h3 className="font-display text-2xl text-slate-100">Delete photo?</h3>
              <p className="mt-2 text-slate-300">
                This will permanently remove the photo from this device.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete.id)}
                  className="h-11 px-5 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm"
                  data-testid={`${testIdPrefix}-delete-confirm`}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
